import { Theme, useMediaQuery, useTheme } from '@mui/material';

interface UseUIReturn {
    /**
     * @description The current theme.
     */
    theme: Theme;
    /**
     * @description Whether the user has dark mode enabled.
     */
    darkMode: boolean;
    /**
     * @description Whether the user is on a mobile device.
     */
    isMobile: boolean;
    /**
     * @description Whether the user is on a tablet device.
     */
    isTablet: boolean;
    /**
     * @description Whether the user is on a desktop device.
     */
    isDesktop: boolean;
    /**
     * @description Whether the user is on an extra small screen device.
     */
    isXs: boolean;
    /**
     * @description Whether the user is on a mobile or tablet device.
     */
    isMobileOrTablet: boolean;
}

/**
 * @description Hook that provides UI related information.
 * @returns {UseUIReturn} An object containing the theme, dark mode, and breakpoints.
 */
function useUI(): UseUIReturn {
    const theme = useTheme();
    const darkMode = useMediaQuery('(prefers-color-scheme: dark)');

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isXs = useMediaQuery(theme.breakpoints.down('xs'));

    return {
        theme,
        darkMode,
        isMobile,
        isTablet,
        isDesktop,
        isXs,
        isMobileOrTablet: isMobile || isTablet,
    };
}

export { useUI };
